import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

// Define DUNA validation status (based on last year)
export type DunaValidationStatus =
  | 'valid'
  | 'attention'
  | 'no_data'
  | 'no_paths';

@Component({
  selector: 'app-duna-validation-badge',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
  templateUrl: './duna-validation-badge.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-validation-badge.component.scss',
})
export class DunaValidationBadgeComponent {
  @Input() status: DunaValidationStatus = 'attention';
  @Input() isDetailBadge: boolean = false;
  @Input() customTooltip?: string;

  /**
   * Gets display text for DUNA validation status
   */
  getValidationText(): string {
    switch (this.status) {
      case 'valid':
        return 'DUNA Gyldig';
      case 'attention':
        return 'DUNA Opmærksom';
      case 'no_data':
        return 'DUNA Ny';
      case 'no_paths':
        return 'DUNA Ingen uddannelsesveje';
      default:
        return 'DUNA Opmærksom';
    }
  }

  /**
   * Gets icon for DUNA validation status
   */
  getValidationIcon(): string {
    switch (this.status) {
      case 'valid':
        return 'check_circle';
      case 'attention':
        return 'warning';
      case 'no_data':
        return 'info';
      case 'no_paths':
        return 'info';
      default:
        return 'warning';
    }
  }

  /**
   * Gets tooltip text for DUNA validation status
   */
  getValidationTooltip(): string {
    if (this.customTooltip) {
      return this.customTooltip;
    }

    const lastYear = new Date().getFullYear() - 1;

    switch (this.status) {
      case 'valid':
        return `Uddannelsesstien er gyldig baseret på deltagelse i ${lastYear}`;
      case 'attention':
        return `Uddannelsesstien kræver opmærksomhed - tjek venligst deltagelse og moduler for ${lastYear}`;
      case 'no_data':
        return `Ingen data tilgængelig for ${lastYear} - dette er normalt for nye brugere`;
      case 'no_paths':
        return `Ingen uddannelsesveje tilgængelige for ${lastYear}`;
      default:
        return `Uddannelsesstien kræver opmærksomhed`;
    }
  }

  /**
   * Gets CSS classes for the badge
   */
  getCssClasses(): { [key: string]: boolean } {
    const classes: { [key: string]: boolean } = {};
    classes[this.status] = true;
    classes['detail-badge'] = this.isDetailBadge;

    return classes;
  }
}
